
/**
 * Module dependencies.
 */

import { FieldAlert } from './field-alert';
import { compact } from 'lodash';
import { useFormErrorsBasePath } from './form-errors-base-path-context';
import { useRef } from 'react';
import { useTranslate } from '../../i18n';

/**
 * `ErrorType` type.
 */

type ErrorType = 'error' | 'warning';

/**
 * `Error` type.
 */

type Error = {
  args?: any;
  rule: string;
};

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  error: Error | null | undefined;
  hasIcon?: boolean;
  isOpen?: boolean;
  name: string;
  type?: ErrorType;
};

/**
 * Get error paths.
 */

const getErrorPaths = (
  basePath: string | null | undefined,
  errorRule: string | null | undefined,
  field: string | null | undefined
): Array<string> => compact([
  basePath && field && errorRule ? `${basePath}.${field}.${errorRule}` : null,
  basePath && field ? `${basePath}.${field}.defaultError` : null,
  field && errorRule ? `defaultErrors.form.${field}.${errorRule}` : null,
  field ? `defaultErrors.form.${field}.defaultError` : null,
  basePath && errorRule ? `${basePath}.fallbacks.${errorRule}` : null,
  basePath ? `${basePath}.fallbacks.defaultError` : null,
  errorRule ? `defaultErrors.form.fallbacks.${errorRule}` : null,
  'defaultErrors.form.fallbacks.defaultError'
]);

/**
 * Export `FieldError` component.
 */

export function FieldError(props: Props): JSX.Element {
  const { error, hasIcon = true, name, type = 'error', ...rest } = props;
  const { translate } = useTranslate();
  const basePath = useFormErrorsBasePath();
  const errorRef = useRef<string | null>(null);
  const typeRef = useRef<ErrorType | null>(null);

  if (error) {
    // Keep the current error on a ref so that it keeps being shown
    // while the collapse is closing.
    errorRef.current = translate(getErrorPaths(basePath, error.rule, name), error.args);
    typeRef.current = type;
  }

  return (
    <FieldAlert
      {...rest}
      hasIcon={hasIcon}
      type={typeRef.current ?? type}
    >
      {errorRef.current}
    </FieldAlert>
  );
}
